import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/solid-router";
import { createSignal } from "solid-js";
import { useCreateSourceMutation } from "~/lib/queries/sources";

export const Route = createFileRoute(
  "/_authenticated/projects/$projectId/sources/new",
)({
  ssr: false,
  component: NewSourcePage,
});

function NewSourceForm(props: {
  projectId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [citation, setCitation] = createSignal("");
  const [description, setDescription] = createSignal("");
  const [comments, setComments] = createSignal("");
  const [contextDate, setContextDate] = createSignal("");
  const [citationError, setCitationError] = createSignal("");

  const createSource = useCreateSourceMutation(() => props.projectId);

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!citation().trim()) {
      setCitationError("Citation is required");
      return;
    }
    setCitationError("");
    await createSource.mutateAsync({
      citation: citation().trim(),
      description: description().trim() || undefined,
      comments: comments().trim() || undefined,
      contextDate: contextDate().trim() || undefined,
    });
    props.onSuccess();
  };

  const inputClass =
    "w-full h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground " +
    "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 " +
    "focus-visible:ring-ring transition-colors";

  const labelClass = "block text-sm font-medium text-foreground mb-1.5";

  return (
    <form onSubmit={handleSubmit} class="space-y-4">
      <div>
        <label for="source-citation" class={labelClass}>
          Citation{" "}
          <span class="text-destructive" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id="source-citation"
          type="text"
          placeholder="e.g., Smith Family Bible, births registry, p. 12"
          value={citation()}
          onInput={(e) => setCitation(e.currentTarget.value)}
          class={`${inputClass} ${citationError() ? "border-destructive focus-visible:ring-destructive/50" : ""}`}
          autocomplete="off"
        />
        {citationError() && (
          <p class="mt-1.5 text-xs text-destructive">{citationError()}</p>
        )}
      </div>

      <div>
        <label for="source-description" class={labelClass}>
          Description{" "}
          <span class="text-muted-foreground text-xs font-normal">
            (optional)
          </span>
        </label>
        <textarea
          id="source-description"
          rows="2"
          placeholder="A brief description of this source…"
          value={description()}
          onInput={(e) => setDescription(e.currentTarget.value)}
          class={`${inputClass} h-auto py-2 resize-none`}
        />
      </div>

      <div>
        <label for="source-comments" class={labelClass}>
          Comments{" "}
          <span class="text-muted-foreground text-xs font-normal">
            (optional)
          </span>
        </label>
        <textarea
          id="source-comments"
          rows="2"
          placeholder="Additional researcher notes, research log details…"
          value={comments()}
          onInput={(e) => setComments(e.currentTarget.value)}
          class={`${inputClass} h-auto py-2 resize-none`}
        />
      </div>

      <div>
        <label for="source-context-date" class={labelClass}>
          Context Date{" "}
          <span class="text-muted-foreground text-xs font-normal">
            (optional)
          </span>
        </label>
        <input
          id="source-context-date"
          type="text"
          placeholder="e.g., circa 1850, 1910–1920"
          value={contextDate()}
          onInput={(e) => setContextDate(e.currentTarget.value)}
          class={inputClass}
          autocomplete="off"
        />
        <p class="mt-1 text-[11px] text-muted-foreground">
          Free-text representation of when this record was compiled or occurred.
        </p>
      </div>

      {createSource.isError && (
        <p class="text-xs text-destructive">
          Failed to create source. Please try again.
        </p>
      )}

      <div class="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={props.onCancel}
          disabled={createSource.isPending}
          class="h-9 px-4 rounded-lg border border-border bg-transparent text-sm font-medium
                 text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={createSource.isPending}
          class="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium
                 hover:bg-primary/90 transition-colors disabled:opacity-70
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {createSource.isPending ? "Creating…" : "Create source"}
        </button>
      </div>
    </form>
  );
}

function NewSourcePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = Route.useParams();

  const isMasked = () => !!location().maskedLocation;

  const onSuccess = () =>
    navigate({
      to: "/projects/$projectId/sources",
      params: { projectId: params().projectId },
      replace: true,
    });
  const onCancel = () =>
    navigate({
      to: "/projects/$projectId/sources",
      params: { projectId: params().projectId },
      replace: true,
    });

  if (isMasked()) {
    return (
      <>
        {/* Backdrop */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: Backdrop dismisses modal */}
        <div
          class="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={onCancel}
          role="presentation"
        />
        {/* Modal content */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-source-title"
          class="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2
                 w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl
                 animate-in fade-in zoom-in-95 duration-150"
        >
          <h2
            id="new-source-title"
            class="text-base font-semibold text-foreground mb-4"
          >
            New Source
          </h2>
          <NewSourceForm
            projectId={params().projectId}
            onSuccess={onSuccess}
            onCancel={onCancel}
          />
        </div>
      </>
    );
  }

  return (
    <div class="max-w-lg mx-auto">
      <h1 class="text-xl font-semibold text-foreground mb-6">New Source</h1>
      <div class="rounded-xl border border-border bg-card p-6">
        <NewSourceForm
          projectId={params().projectId}
          onSuccess={onSuccess}
          onCancel={onCancel}
        />
      </div>
    </div>
  );
}
